import { PromptTool, PromptVariable } from './components/VoiceAgentPromptEditor.tsx';

// Dynamic variables available in the conversation context. They mirror the backend VoiceDynamicVariables plus the
// ElevenLabs system variables, grouped by category in the palette. Referencing one in the prompt inserts it with the
// {{ }} template syntax.
export const PROMPT_VARIABLES: PromptVariable[] = [
    {
        name: 'caller_name',
        category: 'caller',
        label: 'Caller name',
        description: "The caller's name, resolved from the contact lookup",
    },
    {
        name: 'caller_phone_number',
        category: 'caller',
        label: 'Caller phone number',
        description: "The caller's phone number, set at the start of the call",
    },
    {
        name: 'client_name',
        category: 'client',
        label: 'Client name',
        description: 'The matched customer / company name',
    },
    {
        name: 'client_id',
        category: 'client',
        label: 'Client ID',
        description: 'The ERP customer (partition) id of the caller',
    },
    { name: 'contact_id', category: 'caller', label: 'Contact ID', description: 'The contact person id' },
    { name: 'client_street', category: 'client', label: 'Client street', description: "The client's street address" },
    { name: 'client_zip_code', category: 'client', label: 'Client ZIP code', description: "The client's postal code" },
    { name: 'client_city', category: 'client', label: 'Client city', description: "The client's city" },
    { name: 'client_country', category: 'client', label: 'Client country', description: "The client's country" },
    {
        name: 'internal_contact_id',
        category: 'internalContact',
        label: 'Internal contact ID',
        description: 'ID of the internal contact the caller was identified as (e.g. a field sales person)',
    },
    {
        name: 'internal_contact_name',
        category: 'internalContact',
        label: 'Internal contact name',
        description: 'Name of the internal contact the caller was identified as',
    },
    {
        name: 'internal_contact_email',
        category: 'internalContact',
        label: 'Internal contact email',
        description: 'Email of the internal contact the caller was identified as',
    },
    {
        name: 'internal_contact_phone',
        category: 'internalContact',
        label: 'Internal contact phone',
        description: 'Phone number of the internal contact the caller was identified as',
    },
    {
        name: 'system__conversation_id',
        category: 'system',
        label: 'Conversation ID',
        description: 'ElevenLabs conversation id (system variable)',
    },
    {
        name: 'system__caller_id',
        category: 'system',
        label: 'Caller phone number',
        description: "The caller's phone number (system variable)",
    },
    {
        name: 'system__call_sid',
        category: 'system',
        label: 'Call SID',
        description: 'Twilio call SID (system variable)',
    },
];

// The personalized greeting is spoken before any tool ran, so it can only use what caller identification fills at
// call start. The company name follows once the backend fills it there too (ENG-11383).
const GREETING_VARIABLE_NAMES = new Set(['caller_name']);
export const GREETING_VARIABLES: PromptVariable[] = PROMPT_VARIABLES.filter((v) => GREETING_VARIABLE_NAMES.has(v.name));

// Human-readable descriptions for the configurable + built-in tools, surfaced in the prompt editor palette.
export const TOOL_DESCRIPTIONS: Record<string, string> = {
    article_search: 'Search the product catalog by article number or description',
    order_history: "Look up the customer's past orders",
    order_lookup:
        'Look up one order by its order number for callers whose company is not identified — returns only the order date, the articles with their extra data and a masked customer number',
    open_offers: 'Look up open quotes / offers for the customer',
    delivery_address: 'Retrieve known delivery addresses for the customer',
    client_search:
        'Find a client by company name or client id (customer number), returning its client id and full address',
    internal_contact_search: 'Look up an internal contact (e.g. the field sales person) by name, email or phone',
    forward_to_internal_number:
        'Forward the caller to the internal contact found via internal_contact_search (through transfer_to_number; ' +
        'when the Teams status check is enabled, only contacts with a green or yellow status are forwarded to)',
    get_teams_status:
        'Check the Microsoft Teams status of an internal contact by email (green, yellow, red or offline)',
    send_teams_message: 'Send a Microsoft Teams chat message to the internal contact found via internal_contact_search',
    send_email: 'Send an email notification to an internal contact',
    create_order: 'Submit the collected order to create an order record',
    create_rfq: 'Submit the collected information to create a price-quote (RFQ) record',
};

const END_CALL_TOOL: PromptTool = { name: 'end_call', description: 'End the conversation' };
const TRANSFER_TO_NUMBER_TOOL: PromptTool = {
    name: 'transfer_to_number',
    description:
        'Transfer the caller to a human operator (when a transfer number is set) or to an internal contact ' +
        '(when forwarding is enabled)',
};

// ElevenLabs built-ins: end_call always exists, transfer_to_number only when there is something to dial
export const hasTransferToNumber = (enabledToolNames: string[], transferPhoneNumber?: string): boolean =>
    Boolean(transferPhoneNumber?.trim()) || enabledToolNames.includes('forward_to_internal_number');

export const toolsForPrompt = (enabledToolNames: string[], transferPhoneNumber?: string): PromptTool[] => [
    ...enabledToolNames.map((name) => ({ name, description: TOOL_DESCRIPTIONS[name] })),
    END_CALL_TOOL,
    ...(hasTransferToNumber(enabledToolNames, transferPhoneNumber) ? [TRANSFER_TO_NUMBER_TOOL] : []),
];
